import { TextPlaceholder } from '@features/l10n/Text.js';
import { type Breakpoint, useBreakpointMatches } from '@shared/breakpoints.js';
import {
  createContext,
  Fragment,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';

const InTooltip = createContext(false);

// Lets a nested tooltip's target (a badge on a button) hide the enclosing one.
const SuppressParent = createContext<((active: boolean) => void) | null>(null);

/**
 * Whether this is a tooltip's own body. A mark rendered there is already being
 * explained, so it drops to its glyph rather than offering a tooltip of its own
 * — see `GlyphMarker`.
 */
export function useInTooltip(): boolean {
  return useContext(InTooltip);
}

/**
 * What the render prop hands the control being explained: the tooltip's own
 * ref and the gestures that open it. A control that isn't an element of the
 * caller's own — a component wrapping its own button — takes these through a
 * prop of its own and spreads them there.
 */
export type TooltipTargetProps = {
  ref: (el: HTMLElement | null) => void;
  onPointerEnter: (e: PointerEvent) => void;
  onPointerLeave: (e: PointerEvent) => void;
  onClickCapture: (e: MouseEvent) => void;
  onContextMenuCapture: (e: MouseEvent) => void;
};

const noop = () => {};

/** What a disabled control is handed: its wrapper is the tooltip's target. */
const INERT_TARGET: TooltipTargetProps = {
  ref: noop,
  onPointerEnter: noop,
  onPointerLeave: noop,
  onClickCapture: noop,
  onContextMenuCapture: noop,
};

type Props = {
  label?: ReactNode;
  /**
   * Name of the control. When set, the tooltip shows on hover/long-press even
   * while the inline label is visible: expanded it reads as `name`, collapsed as
   * `name: label` (so the hidden value stays discoverable).
   */
  name?: ReactNode;
  delay?: number;
  breakpoint?: Breakpoint;
  kbd?: string;
  /**
   * A line under the label — what the control's own words don't say (e.g. that
   * the value on show is the one a free account is held to).
   */
  hint?: ReactNode;
  /**
   * Overrides `breakpoint` where the room is measured rather than assumed —
   * see `FmModalFooter`.
   */
  hideLabel?: boolean;
  /**
   * Opens on a plain click or tap as well. Only for a mark that does nothing
   * else — on a control the same tap would activate it.
   */
  toggleOnClick?: boolean;
  /**
   * The control is disabled, so takes no pointer events: the tooltip hangs on a
   * wrapper around it instead, and the control is handed inert props.
   */
  disabled?: boolean;
  /** Classes for that wrapper; `d-inline-block` by default. */
  wrapperClassName?: string;
  children: (props: {
    props: TooltipTargetProps;
    label: ReactNode;
    labelClassName: string;
  }) => ReactNode;
};

export function LongPressTooltip({
  label = <TextPlaceholder />,
  name,
  kbd,
  hint,
  delay = 500,
  breakpoint,
  hideLabel,
  toggleOnClick,
  disabled,
  wrapperClassName = 'd-inline-block',
  children,
}: Props) {
  const [show, setShow] = useState(false);

  const suppressParent = useContext(SuppressParent);

  const [childActive, setChildActive] = useState(false);

  const [target, setTarget] = useState<HTMLElement | null>(null);

  // Stable, or React detaches and re-attaches the ref on every render.
  const attachTarget = useCallback((el: HTMLElement | null) => {
    setTarget(el);
  }, []);

  // Whether the pointer that opened the tooltip was a finger or a stylus, which
  // covers the control it points at and so needs the tooltip pushed clear of it.
  const [coarse, setCoarse] = useState(false);

  // The same, readable from a handler of the gesture that set it.
  const coarseRef = useRef(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const matches = useBreakpointMatches();

  // Named through the tooltip alone where no breakpoint prints the label; the
  // matches may be a panel's own width rather than the viewport's, so the class
  // is decided here rather than by a `d-{bp}-inline` utility.
  const labelHidden =
    hideLabel ??
    (breakpoint === undefined || (breakpoint !== 'xs' && !matches[breakpoint]));

  const handleStart = useCallback(
    (e: PointerEvent) => {
      const isCoarse = e.pointerType === 'touch' || e.pointerType === 'pen';

      coarseRef.current = isCoarse;

      setCoarse(isCoarse);

      suppressParent?.(true);

      if (
        (!labelHidden && name == null && hint == null) ||
        timeoutRef.current
      ) {
        return;
      }

      timeoutRef.current = setTimeout(() => {
        setShow(true);
      }, delay);
    },
    [delay, hint, labelHidden, name, suppressParent],
  );

  const handleClear = useCallback(() => {
    suppressParent?.(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = null;

    // A finger's pointerleave is the lift, not a move away, and it lands before
    // the click — closing here would leave the tap below with nothing to close.
    if (!(toggleOnClick && coarseRef.current)) {
      setShow(false);
    }
  }, [toggleOnClick, suppressParent]);

  const handleClickCapture = useCallback(
    (e: MouseEvent) => {
      // Inside a `<label>` the tap would otherwise focus the labelled control
      // and forward a click to it, which `rootClose` reads as a click outside.
      if (toggleOnClick) {
        e.preventDefault();
      }

      setShow((show) => (toggleOnClick ? !show : false));
    },
    [toggleOnClick],
  );

  // Stops the native menu on a long press. The tap that ended it raises no
  // click of its own — the browser has already cancelled the gesture.
  const handleContextMenuCapture = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // The parameter must not be named `kbd`: a binding that shadows an intrinsic
  // tag name makes the React Compiler rename the `<kbd>` tag itself, and the
  // element renders as the unknown `<kbd_0>`.
  const kbdEl = (kbd?.split(' ') ?? []).map((keyName) => (
    <Fragment key={keyName}>
      {' '}
      <kbd>{keyName}</kbd>
    </Fragment>
  ));

  const tooltipBody =
    name == null ? (
      // Nameless and already legible: only a hint has anything left to say.
      labelHidden ? (
        label
      ) : null
    ) : labelHidden ? (
      <>
        {name}: {label}
      </>
    ) : (
      name
    );

  // Undefined for a mouse, so the tooltip keeps Bootstrap's own offset.
  const offset = useMemo(
    () => (coarse ? ([0, 30] as [number, number]) : undefined),
    [coarse],
  );

  // Near the top edge the preferred `top` placement flips, and for a finger
  // `bottom` would land right under the fingertip; sidestep to a side first.
  const popperConfig = useMemo(
    () =>
      coarse
        ? {
            modifiers: [
              {
                name: 'flip',
                options: { fallbackPlacements: ['right', 'left', 'bottom'] },
              },
            ],
          }
        : undefined,
    [coarse],
  );

  const targetProps: TooltipTargetProps = {
    ref: attachTarget,
    onPointerEnter: handleStart,
    onPointerLeave: handleClear,
    onClickCapture: handleClickCapture,
    onContextMenuCapture: handleContextMenuCapture,
  };

  const control = children({
    props: disabled ? INERT_TARGET : targetProps,
    label: kbd ? (
      <>
        {label} {kbdEl}
      </>
    ) : (
      label
    ),
    labelClassName: labelHidden ? 'd-none' : 'd-inline',
  });

  return (
    <>
      <SuppressParent value={setChildActive}>
        {disabled ? (
          <span className={wrapperClassName} {...targetProps}>
            {control}
          </span>
        ) : (
          control
        )}
      </SuppressParent>

      {target && (labelHidden || name != null || hint != null) && (
        <Overlay
          target={target}
          show={show && !childActive}
          placement="top"
          flip
          offset={offset}
          popperConfig={popperConfig}
          rootClose={toggleOnClick}
          // The click on the mark itself reaches here too, after the capture
          // handler above has already toggled it.
          onHide={(e) => {
            if (!target.contains(e.target as Node)) {
              setShow(false);
            }
          }}
        >
          {(props) => (
            <Tooltip {...props}>
              <InTooltip value>
                {tooltipBody}
                {kbdEl}
                {hint != null && <div className="mt-1">{hint}</div>}
              </InTooltip>
            </Tooltip>
          )}
        </Overlay>
      )}
    </>
  );
}
